import { Component, inject } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CommonModule } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { finalize } from 'rxjs';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { LoaderService } from '../../../shared/services/loader/loader.service';
import { HolidaysService } from '../../../shared/services/holidays/holidays.service';
import { StorageService } from '../../../shared/services/storage/storage.service';
import { RolesService } from '../../../shared/services/roles/roles.service';

@Component({
  selector: 'app-protocol-form',
  imports: [
    CommonModule,
    MatIconModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonModule,
    MatCheckboxModule,
    FormsModule,
    RouterLink,
    LoaderComponent,
  ],
  templateUrl: './protocol-form.component.html',
  styleUrls: ['./protocol-form.component.css', '../../../shared/styles/layout.css'],
})
export class ProtocolFormComponent {
  // Mode management
  isEditMode: boolean = false;
  protocolId: number | null = null;

  // Common properties
  myroles: any = {};
  protocolForm: FormGroup;
  protocol: any = {};
  isSubmitting = false;
  attemptedSubmit = false;

  private _snackBar = inject(MatSnackBar);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private holidaysService: HolidaysService,
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private loaderService: LoaderService,
    private storageService: StorageService,
    private rolesService: RolesService
  ) {
    this.protocolForm = this.fb.group({
      name: ['', [Validators.required]],
      code: ['', [Validators.required]],
      description: ['', [Validators.required]],
      enabled: [true],
    });
  }

  get isLoading() {
    return this.loaderService.getLoader();
  }

  ngOnInit() {
    this.route.params.subscribe((params) => {
      if (params['id']) {
        this.isEditMode = true;
        this.protocolId = +params['id'];
        this.loaderService.showLoader();
        this.loadProtocolData();
      } else {
        this.isEditMode = false;
        this.protocolId = null;
      }
    });
    this.loadRoles();
  }

  loadRoles() {
    const permissions = this.rolesService.getPermissionsForFunctionality(
      'Vacaciones',
      this.storageService
    );

    if (permissions) {
      this.myroles = permissions;

      const permissionMessage = this.isEditMode
        ? 'No tienes permisos para editar un protocolo'
        : 'No tienes permisos para añadir un protocolo';

      this.rolesService.checkWritePermission(
        'Vacaciones',
        this.storageService,
        this._snackBar,
        this.router,
        '/home/vacations/drivers',
        permissionMessage
      );
    }
  }

  private loadProtocolData(): void {
    if (!this.protocolId) return;

    // TODO: Implement getProtocolById in HolidaysService
    // For now, we'll just hide the loader
    this.loaderService.hideLoader();
    this.snackBar.open(
      'Funcionalidad de carga pendiente de implementación en el servicio',
      'Cerrar',
      { duration: 5000 }
    );

    /* Uncomment when service method is ready:
    this.holidaysService.getProtocolById(this.protocolId).subscribe({
      next: (data) => {
        this.protocol = data;

        // Set the form values from the loaded protocol data
        this.protocolForm.patchValue({
          name: this.protocol.name,
          code: this.protocol.code,
          description: this.protocol.description,
          enabled: this.protocol.enabled,
        });
        this.loaderService.hideLoader();
      },
      error: (error) => {
        this.loaderService.hideLoader();
        console.error('Error fetching protocol data', error);
        this.snackBar.open('Error al cargar los datos', 'Cerrar', {
          duration: 3000,
        });
      },
    });
    */
  }

  saveChanges(): void {
    this.loaderService.showLoader();
    if (this.isSubmitting) return;
    this.attemptedSubmit = true;

    if (!this.protocolForm.valid) {
      this.loaderService.hideLoader();
      // Mark all controls as touched to trigger validation visuals
      Object.keys(this.protocolForm.controls).forEach((key) => {
        const control = this.protocolForm.get(key);
        control?.markAsTouched();
      });

      this.snackBar.open(
        'Por favor completa todos los campos requeridos',
        'Cerrar',
        { duration: 5000 }
      );
      return;
    }

    this.isSubmitting = true;

    if (this.isEditMode) {
      this.updateProtocol();
    } else {
      this.createProtocol();
    }
  }

  private createProtocol(): void {
    const formValue = this.protocolForm.value;

    const body = {
      name: formValue.name,
      code: formValue.code,
      description: formValue.description,
      enabled: formValue.enabled,
    };

    // TODO: Implement the post method in HolidaysService
    // For now, we'll show a message
    this.loaderService.hideLoader();
    this.isSubmitting = false;
    this.snackBar.open(
      'Funcionalidad de creación pendiente de implementación en el servicio',
      'Cerrar',
      { duration: 5000 }
    );
    console.log('Protocol to create:', body);

    // Uncomment when the service method is ready:
    /*
    this.holidaysService
      .postProtocol(body)
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: (response) => {
          this.loaderService.hideLoader();
          this.snackBar.open('Protocolo creado exitosamente', 'Cerrar', {
            duration: 3000,
          });
          this.router.navigate(['/home/vacations/drivers']);
        },
        error: (error) => {
          this.loaderService.hideLoader();
          console.error('Error creating protocol:', error);
          this.snackBar.open('Error al crear el protocolo', 'Cerrar', {
            duration: 3000,
          });
        },
      });
    */
  }

  private updateProtocol(): void {
    if (!this.protocolId) return;

    const formValue = this.protocolForm.value;

    const body = {
      name: formValue.name,
      code: formValue.code,
      description: formValue.description,
      enabled: formValue.enabled,
    };

    // TODO: Implement the put method in HolidaysService
    // For now, we'll show a message
    this.loaderService.hideLoader();
    this.isSubmitting = false;
    this.snackBar.open(
      'Funcionalidad de actualización pendiente de implementación en el servicio',
      'Cerrar',
      { duration: 5000 }
    );
    console.log('Protocol to update:', body);

    // Uncomment when the service method is ready:
    /*
    this.holidaysService
      .putProtocol(this.protocolId, body)
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: (response) => {
          this.loaderService.hideLoader();
          this.snackBar.open('Protocolo actualizado correctamente', 'Cerrar', {
            duration: 3000,
          });
          this.router.navigate(['/home/vacations/drivers']);
        },
        error: (error) => {
          console.error('Error updating protocol:', error);
          this.loaderService.hideLoader();
          this.snackBar.open('Error al actualizar el protocolo', 'Cerrar', {
            duration: 3000,
          });
        },
      });
    */
  }

  cancel(): void {
    this.router.navigate(['/home/vacations/drivers']);
  }
}
